import React from "react";
import {
  Box,
  TextField as MuiTextField,
  Grid,
  Select,
  MenuItem,
  Typography,
  Stack,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";

let currentdtemp = new Date();
const year = currentdtemp.getFullYear();
const month = String(currentdtemp.getMonth() + 1).padStart(2, "0");
const day = String(currentdtemp.getDate()).padStart(2, "0");

const bgColor = "rgb(210 210 210 / 10%)";
const bgColorDisabled = "rgb(210 210 210 / 12%)";

const FieldViewIcon = ({ handleIconClick, isPinned }) => (
  <VisibilityIcon
    fontSize={"small"}
    className="text-gray-400 hover:text-red-500 transition-colors"
    sx={{
      color: isPinned ? "rgba(0, 171, 78, 1)" : "rgba(0, 171, 78, 0.5)",
    }}
    style={{ flexShrink: 0 }}
    onClick={handleIconClick}
  />
);
const CustomTextField = ({
  pk,
  keyName,
  editable,
  patientErrorMessage,
  editeValue,
  type = "text",
  setHoveredFieldKey,
  highlightData,
  pinnedHighlights,
  togglePinnedHighlight,
  ...fieldProps
}) => {
  const formatDateValue = (value) => {
    if (type === "date") {
      if (editable) {
        // For editable date inputs, ensure YYYY-MM-DD format
        if (!value) return "";
        // If it's already in correct format, return as is
        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
        // Convert from MM/DD/YYYY to YYYY-MM-DD
        const parts = value.split("/");
        if (parts.length === 3) {
          return `${parts[2]}-${parts[0].padStart(2, "0")}-${parts[1].padStart(
            2,
            "0",
          )}`;
        }
        return value;
      } else {
        // For non-editable, keep MM/DD/YYYY format
        return value || "";
      }
    }
    return value || "";
  };

  const getHighlightKey = (bindValue, keyName) => {
    const mappings = {
      FirstName: "Patient.Patient Name.Patient First Name",
      LastName: "Patient.Patient Name.Patient Last Name",
      Gender: "Patient.Patient Gender",
      DateOfBirth: "Patient.Patient DOB",
      Contact: "Patient.Patient Phone.Patient Phone Cell",
      ProviderFirstName: "Provider.Provider First Name",
      ProviderLastName: "Provider.Provider Last Name",
      ProviderPhone: "Provider.Provider Phone",
      ProviderFax: "Provider.Provider Fax",
      ProviderNPI: "Provider.Provider NPI",
      DateWritten: "Date Written",
      LengthOfNeed: "Length of Need",
      DiagnosisCode: "Diagnosis Code",
    };
    return mappings[pk?.labelBindValue];
  };

  const highlightKey = getHighlightKey(pk?.labelBindValue, keyName);
  const hasCoordinates =
    highlightKey && highlightData?.coordinates?.[highlightKey];

  const handleMouseEnter = () => {
    if (highlightKey && hasCoordinates) {
      setHoveredFieldKey(highlightKey);
    }
  };

  const handleMouseLeave = () => {
    setHoveredFieldKey(null);
  };

  const isPinned = pinnedHighlights.has(highlightKey);

  const handleIconClick = (e) => {
    e.stopPropagation();
    if (hasCoordinates) {
      togglePinnedHighlight(highlightKey);
    }
  };

  return (
    <div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        cursor: hasCoordinates ? "pointer" : "default",
      }}
    >
      <MuiTextField
        sx={{
          "& .MuiFilledInput-root": {
            bgcolor: bgColor,
            "&.Mui-disabled": {
              bgcolor: bgColorDisabled,
            },
          },
        }}
        size="small"
        label={pk?.labelName}
        variant="filled"
        fullWidth
        disabled={!editable}
        value={formatDateValue(pk?.labelValue)}
        error={Boolean(
          patientErrorMessage[keyName]?.[pk?.labelBindValue]?.length,
        )}
        helperText={patientErrorMessage[keyName]?.[pk?.labelBindValue] || ""}
        required={
          pk?.labelBindValue !== "ProviderFax" && Boolean(pk?.labelStartColor)
        }
        onChange={editable ? (e) => editeValue(e, keyName) : undefined}
        type={type}
        name={pk?.labelBindValue}
        {...fieldProps}
      />
      {hasCoordinates && (
        <FieldViewIcon handleIconClick={handleIconClick} isPinned={isPinned} />
      )}
    </div>
  );
};

const AddressField = ({
  pk,
  keyName,
  editable,
  patientErrorMessage,
  handleAddressLineChange,
  editeValue,
  setHoveredFieldKey,
  highlightData,
  pinnedHighlights,
  togglePinnedHighlight,
  ...fieldProps
}) => {
  const getAddressHighlightKey = (field, keyName) => {
    const mappings = {
      addressLine:
        keyName === "patientKeyValues"
          ? "Patient.Patient Address.Patient Street Line 1"
          : "Provider.Provider Address",
      city:
        keyName === "patientKeyValues"
          ? "Patient.Patient Address.Patient City"
          : "Provider.Provider City",
      state:
        keyName === "patientKeyValues"
          ? "Patient.Patient Address.Patient State"
          : "Provider.Provider State",
      zip:
        keyName === "patientKeyValues"
          ? "Patient.Patient Address.Patient Zip"
          : "Provider.Provider Zip",
    };
    return mappings[field];
  };

  const error = (field) =>
    Boolean(
      patientErrorMessage[keyName]?.[`${pk?.labelBindValue}`]?.[field]?.length,
    );

  const helperText = (field) =>
    patientErrorMessage[keyName]?.[`${pk?.labelBindValue}`]?.[field];

  const commonProps = {
    fullWidth: true,
    size: "small",
    required: Boolean(pk?.labelStartColor),
    disabled: !editable,
  };

  const hasCoordinates = (field) => {
    const highlightKey = getAddressHighlightKey(field, keyName);
    return highlightKey && highlightData?.coordinates?.[highlightKey];
  };

  const handleMouseEnter = (field) => {
    const highlightKey = getAddressHighlightKey(field, keyName);
    if (highlightKey && hasCoordinates(field)) {
      setHoveredFieldKey(highlightKey);
    }
  };

  const handleMouseLeave = () => {
    setHoveredFieldKey(null);
  };

  const handleIconClick = (field, e) => {
    e.stopPropagation();
    const highlightKey = getAddressHighlightKey(field, keyName);
    if (highlightKey && hasCoordinates(field)) {
      togglePinnedHighlight(highlightKey);
    }
  };

  const isPinned = (field) => {
    const highlightKey = getAddressHighlightKey(field, keyName);
    return pinnedHighlights.has(highlightKey);
  };

  return (
    <Stack sx={{ mt: 1, mb: 1 }}>
      {/* Address Line */}
      <div
        onMouseEnter={() => handleMouseEnter("addressLine")}
        onMouseLeave={handleMouseLeave}
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: "8px",
          cursor: hasCoordinates("addressLine") ? "pointer" : "default",
          marginBottom: "16px",
        }}
      >
        <MuiTextField
          {...commonProps}
          multiline
          sx={{
            "& .MuiFilledInput-root": {
              bgcolor: bgColor,
              "&.Mui-disabled": {
                bgcolor: bgColorDisabled,
              },
            },
          }}
          variant="filled"
          rows={1}
          label={pk?.labelName ?? "Patient Address"}
          name="addressLine"
          placeholder={editable ? "Address line 1" : undefined}
          value={pk?.labelValue?.addressLine || ""}
          onChange={
            editable ? (e) => handleAddressLineChange(e, keyName) : undefined
          }
          error={error("addressLine")}
          helperText={helperText("addressLine")}
          {...fieldProps}
        />
        {hasCoordinates("addressLine") && (
          <FieldViewIcon
            handleIconClick={(e) => handleIconClick("addressLine", e)}
            isPinned={isPinned("addressLine")}
          />
        )}
      </div>

      {/* City */}
      <div
        onMouseEnter={() => handleMouseEnter("city")}
        onMouseLeave={handleMouseLeave}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "16px",
          cursor: hasCoordinates("city") ? "pointer" : "default",
        }}
      >
        <MuiTextField
          {...commonProps}
          label={editable ? pk?.labelAddressName?.city ?? "City" : "City"}
          name="city"
          sx={{
            "& .MuiFilledInput-root": {
              bgcolor: bgColor,
              "&.Mui-disabled": {
                bgcolor: bgColorDisabled,
              },
            },
          }}
          variant="filled"
          placeholder={editable ? "City" : undefined}
          value={pk?.labelValue?.city || ""}
          onChange={editable ? (e) => editeValue(e, keyName) : undefined}
          error={error("city")}
          helperText={helperText("city")}
        />
        {hasCoordinates("city") && (
          <FieldViewIcon
            handleIconClick={(e) => handleIconClick("city", e)}
            isPinned={isPinned("city")}
          />
        )}
      </div>

      {/* State and Zip in Grid */}
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <div
            onMouseEnter={() => handleMouseEnter("state")}
            onMouseLeave={handleMouseLeave}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: hasCoordinates("state") ? "pointer" : "default",
            }}
          >
            <MuiTextField
              {...commonProps}
              label={
                editable ? pk?.labelAddressName?.state ?? "State" : "State"
              }
              name="state"
              sx={{
                "& .MuiFilledInput-root": {
                  bgcolor: bgColor,
                  "&.Mui-disabled": {
                    bgcolor: bgColorDisabled,
                  },
                },
              }}
              variant="filled"
              placeholder={editable ? "State" : undefined}
              value={pk?.labelValue?.state || ""}
              onChange={editable ? (e) => editeValue(e, keyName) : undefined}
              error={error("state")}
              helperText={helperText("state")}
              inputProps={{ maxLength: 2 }}
            />
            {hasCoordinates("state") && (
              <FieldViewIcon
                handleIconClick={(e) => handleIconClick("state", e)}
                isPinned={isPinned("state")}
              />
            )}
          </div>
        </Grid>
        <Grid item xs={6}>
          <div
            onMouseEnter={() => handleMouseEnter("zip")}
            onMouseLeave={handleMouseLeave}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: hasCoordinates("zip") ? "pointer" : "default",
            }}
          >
            <MuiTextField
              {...commonProps}
              label={editable ? pk?.labelAddressName?.zip ?? "Zip" : "Zip"}
              name="zip"
              variant="filled"
              sx={{
                "& .MuiFilledInput-root": {
                  bgcolor: bgColor,
                  "&.Mui-disabled": {
                    bgcolor: bgColorDisabled,
                  },
                },
              }}
              placeholder={editable ? "Zip" : undefined}
              value={pk?.labelValue?.zip || ""}
              onChange={editable ? (e) => editeValue(e, keyName) : undefined}
              error={error("zip")}
              helperText={helperText("zip")}
              inputProps={{ maxLength: 5 }}
            />
            {hasCoordinates("zip") && (
              <FieldViewIcon
                handleIconClick={(e) => handleIconClick("zip", e)}
                isPinned={isPinned("zip")}
              />
            )}
          </div>
        </Grid>
      </Grid>
    </Stack>
  );
};

const FormFieldHeader = ({ heading }) => {};

const FormFields = ({
  editable,
  processedData,
  patientErrorMessage,
  editeValue,
  handleAddressLineChange,
  orderTypeDropdown,
  setHoveredFieldKey,
  highlightData,
  pinnedHighlights,
  togglePinnedHighlight,
  ...props
}) => {
  const renderField = (pk, keyName) => {
    const fieldType = editable ? pk.labelEditType : pk.labelType;

    switch (fieldType) {
      case "address":
        return (
          <Box sx={{ width: "100%" }}>
            <AddressField
              pk={pk}
              keyName={keyName}
              editable={editable}
              patientErrorMessage={patientErrorMessage}
              handleAddressLineChange={handleAddressLineChange}
              editeValue={editeValue}
              setHoveredFieldKey={setHoveredFieldKey}
              highlightData={highlightData}
              pinnedHighlights={pinnedHighlights}
              togglePinnedHighlight={togglePinnedHighlight}
            />
          </Box>
        );

      case "select":
        if (!editable) return null;
        return (
          <Box sx={{ width: "100%", maxWidth: "100%", mt: 0 }}>
            {pk?.labelGreyout ? (
              <CustomTextField
                pk={pk}
                keyName={keyName}
                editable={editable}
                patientErrorMessage={patientErrorMessage}
                editeValue={editeValue}
                highlightData={highlightData}
                setHoveredFieldKey={setHoveredFieldKey}
                pinnedHighlights={pinnedHighlights}
                togglePinnedHighlight={togglePinnedHighlight}
              />
            ) : (
              <Select
                size="small"
                displayEmpty
                sx={{
                  "& .MuiFilledInput-root": {
                    bgcolor: bgColor,
                    "&.Mui-disabled": {
                      bgcolor: bgColorDisabled,
                    },
                  },
                  "& .MuiSelect-select": {
                    // Targets the select input itself
                    bgcolor: bgColor,
                  },
                  "&.MuiFilledInput-root": {
                    // Targets the root when Select is used directly
                    bgcolor: bgColor,
                  },
                }}
                fullWidth
                variant="filled"
                value={pk?.labelValue || ""}
                name={pk.labelBindValue}
                onChange={(e) => editeValue(e, keyName)}
                error={Boolean(
                  patientErrorMessage[keyName]?.[pk?.labelBindValue]?.length,
                )}
              >
                <MenuItem value="">Gender</MenuItem>
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            )}
          </Box>
        );

      case "text":
      case "number":
      case "date":
        return (
          <CustomTextField
            pk={pk}
            keyName={keyName}
            editable={editable}
            patientErrorMessage={patientErrorMessage}
            editeValue={editeValue}
            type={fieldType}
            setHoveredFieldKey={setHoveredFieldKey}
            highlightData={highlightData}
            pinnedHighlights={pinnedHighlights}
            togglePinnedHighlight={togglePinnedHighlight}
            inputProps={{
              maxLength: fieldType === "number" ? 9 : undefined,
            }}
          />
        );

      default:
        return null;
    }
  };

  if (!processedData || Object.keys(processedData).length === 0) return null;

  return (
    <>
      {Object.keys(processedData).map((keyName, index) => (
        <Box key={keyName}>
          {(keyName === "patientKeyValues" ||
            (orderTypeDropdown !== "Existing" &&
              (keyName === "providerKeyValues" ||
                keyName === "orderKeyValues"))) && (
            <Stack
              direction={"row"}
              spacing={1}
              sx={{
                mb: 2,
                mt: keyName != "patientKeyValues" ? 2 : 0,
                alignItems: "center",
              }}
            >
              {" "}
              {keyName === "patientKeyValues" ? (
                <PersonOutlineIcon fontSize="32px" />
              ) : keyName === "providerKeyValues" ? (
                <PersonAddAltIcon fontSize="32px" />
              ) : (
                <DescriptionOutlinedIcon fontSize="32px" />
              )}
              <Typography
                sx={{
                  fontSize: "1rem",
                }}
              >
                {keyName === "patientKeyValues"
                  ? "Patient Information"
                  : keyName === "providerKeyValues"
                  ? "Provider Information"
                  : "Order Information"}
              </Typography>
            </Stack>
          )}

          <Grid container spacing={2}>
            {processedData[keyName]?.map((pk, fieldIndex) => {
              // Check if this is an order field that should be hidden
              const shouldHideOrderField =
                orderTypeDropdown === "Existing" &&
                keyName === "orderKeyValues";

              if (pk.labelHide === false) {
                if (
                  pk.labelType === "address" ||
                  pk.labelName === "Diagnosis Code"
                ) {
                  return (
                    <Grid
                      item
                      xs={12}
                      key={fieldIndex}
                      sx={{
                        visibility: shouldHideOrderField ? "hidden" : "visible",
                      }}
                    >
                      {renderField(pk, keyName)}
                    </Grid>
                  );
                }
                return (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    key={fieldIndex}
                    sx={{
                      visibility: shouldHideOrderField ? "hidden" : "visible",
                    }}
                  >
                    {renderField(pk, keyName)}
                  </Grid>
                );
              }
              return null;
            })}
          </Grid>
        </Box>
      ))}
    </>
  );
};

export default FormFields;
